// ============================================================================
// SESSION END SERVICE - frontend/src/services/sessionEnd.ts
// Client-side utilities for ending sessions and downloading reports
// ============================================================================

import { api } from './api';

export interface SessionEndResponse {
  success: boolean;
  sessionId: string;
  status: string;
  summary: {
    sessionId: string;
    customerName: string;
    duration: string;
    totalInteractions: number;
    phasesCompleted: string[];
    keyTopics: string[];
    overallSentiment: {
      score: number;
      label: 'positive' | 'neutral' | 'negative';
      analysis: string;
    };
    executiveSummary: string;
  };
  goalsReview: {
    originalGoals: string[];
    goalAnalysis: Array<{
      goal: string;
      addressed: boolean;
      confidence: 'high' | 'medium' | 'low';
      evidence: string[];
      gaps: string[];
    }>;
    overallAlignment: number;
    recommendations: string[];
  };
  reportMarkdown: string;
  requirementsJson: any;
  timestamp: string;
}

/**
 * End a workshop session and generate comprehensive reports
 */
export async function endSession(sessionId: string): Promise<SessionEndResponse> {
  const response = await api.post(`/api/sessions/${sessionId}/end-session`);
  return response.data;
}

/**
 * Reopen a completed session, making it active again
 */
export async function reopenSession(sessionId: string): Promise<void> {
  await api.post(`/api/sessions/${sessionId}/reopen`);
}

/**
 * Download the markdown report as a .md file
 */
export function downloadMarkdownReport(markdown: string, sessionId: string, customerName: string) {
  const filename = `${customerName.replace(/\s+/g, '_')}_Workshop_Report_${sessionId.slice(0, 8)}.md`;
  downloadTextFile(markdown, filename, 'text/markdown');
}

/**
 * Download the requirements JSON as a .json file
 */
export function downloadRequirementsJson(requirements: any, sessionId: string, customerName: string) {
  const filename = `${customerName.replace(/\s+/g, '_')}_Requirements_${sessionId.slice(0, 8)}.json`;
  const jsonString = JSON.stringify(requirements, null, 2);
  downloadTextFile(jsonString, filename, 'application/json');
}

/**
 * Helper function to trigger file download in browser
 */
function downloadTextFile(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Complete workflow: End session and download both reports
 */
export async function endSessionAndDownloadReports(
  sessionId: string,
  onProgress?: (message: string) => void
): Promise<SessionEndResponse> {
  try {
    onProgress?.('Ending session and generating reports...');

    const result = await endSession(sessionId);

    onProgress?.('Session ended successfully. Downloading reports...');

    // Download markdown report
    downloadMarkdownReport(
      result.reportMarkdown,
      result.sessionId,
      result.summary.customerName
    );

    // Download requirements JSON
    downloadRequirementsJson(
      result.requirementsJson,
      result.sessionId,
      result.summary.customerName
    );

    onProgress?.('Reports downloaded successfully!');

    return result;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    onProgress?.(`Error: ${message}`);
    throw error;
  }
}
